/**
 * A setup composed in conversation, and the two functions that stand between
 * it and the database. Phase 1 of docs/plans/ai-setup-and-agent.md.
 *
 * Decision 1 of that plan: a composed setup is a `TemplateSpec` plus a word
 * table. The spec speaks keys, as every spec does, and `words` gives each key
 * the person's own word. `validateTemplateSpec(spec, {...en, ...words})` then
 * checks it with the same validator the five shipped templates pass through,
 * and `applyTemplate` writes it with the same code. No business word enters
 * the schema: the person's words live only in `words`, and reach a row only
 * where a shipped template's word would have - `app.record.name`,
 * `app.field.label`, an option's `label`.
 *
 * Two functions, kept apart on purpose.
 *
 *   readComposedSetup   shape and limits, by hand, in the style of the
 *                       server's `onboarding-input.ts`. It refuses on the
 *                       first fault, because a body that is not the shape is
 *                       not a setup to list problems about.
 *   composeProblems     every problem with a setup that IS the shape, at once,
 *                       so the agent fixes one round trip and not one mistake
 *                       per round trip. Pure, never throws.
 *
 * Links sit beside the spec and not inside it. A `TemplateSpec` has no link
 * vocabulary, and the route that makes a link already has its own validator,
 * `checkSpec`, which runs against real rows and stays the one place the type
 * table is applied. This file checks only what keys can prove: the two
 * records exist, are records rather than lists, differ, and every field named
 * is one they declare. A `ComposedLink` names records and fields by their spec
 * KEYS, and the server rewrites keys to ids at apply time, exactly as it does
 * for the keys inside a view.
 */
import { isCurrencyCode } from './currencies';
import { aggProblems, formulaFieldsRead, formulaHopsRead, isComputedType, readFormulaSpec, type FormulaSpec } from './formula';
import { enFlat, nounShipped } from './i18n/en';
import {
  formulaValueTypeAsFieldType,
  LINK_CONDITION_OPS,
  LINK_MODES,
  LINK_PATTERN_ENGINE,
  LINK_PATTERNS,
  mappingTypeOk,
  type LinkConditionOp,
  type LinkMode,
  type LinkPattern,
} from './links';
import type { SetupUnderstanding } from './setup-understanding';
import { validateTemplateSpec, type Catalogue, type TemplateProblem } from './templates/validate-template';
import type { TemplateFieldSpec, TemplateRecordSpec, TemplateSpec } from './templates/spec';

/* ------------------------------------------------------------------ the shape */

/** The id every composed spec carries. Nothing is stamped on a record from it. */
export const COMPOSED_TEMPLATE_ID = 'composed';

/** Key to the person's own word. Every key here must be absent from `en`. */
export type ComposedWords = Readonly<Record<string, string>>;

/**
 * One comparison inside a composed trigger. `fieldKey` names a field of the
 * FROM record. `value` is one figure or one list of them, and on `is` or
 * `is_not` against a select it names option keys of that field.
 *
 * With `via` set (a `reference` field of the FROM record), the question is
 * asked of the row it points at: `fieldKey` then names a field of the list
 * that reference picks from ("only when the customer's Payment terms are
 * Credit"), `LinkCondition.via` in links.ts.
 */
export type ComposedCondition = { fieldKey: string; op: LinkConditionOp; value?: unknown; via?: string };

/**
 * What fills one target field:
 *
 *   field      a field of the FROM record
 *   const      a fixed value
 *   reference  a field read off the row a FROM reference points at: `via` is
 *              that reference, `fieldKey` a field of its list
 *   formula    a worked-out value, whose `fieldId` slots name FROM fields by
 *              KEY, and whose hops name a field of the list their `via`
 *              points at, exactly as a composed formula field's spec does
 */
export type ComposedMapping =
  | { kind: 'field'; fieldKey: string }
  | { kind: 'const'; value: unknown }
  | { kind: 'reference'; via: string; fieldKey: string }
  | { kind: 'formula'; spec: FormulaSpec };

export type ComposedLink = {
  fromRecordKey: string;
  toRecordKey: string;
  pattern: LinkPattern;
  sign: 1 | -1;
  mode: LinkMode;
  trigger: { all: Array<{ any: Array<ComposedCondition> }> };
  /** Keyed by a field key of the TO record. */
  fieldMap: Record<string, ComposedMapping>;
};

export type ComposedSetup = {
  businessName: string;
  /** ISO 4217. `isCurrencyCode` decides. */
  currency: string;
  /** id `composed`, version 1, every `*Key` a key into `words` or into `en`. */
  spec: TemplateSpec;
  words: ComposedWords;
  links: ComposedLink[];
  /**
   * What the build could not hold with the parts it has (Decision 18 of
   * docs/plans/entry-lines-and-layout.md), carried so the route writes each
   * to app.unmet_need when the person creates the business. Never shown to
   * the person. Absent on a setup that noted none.
   */
  unmetNeeds?: readonly ComposedUnmetNeed[];
};

/**
 * The five levels a part is chosen at (Section 7.5 of
 * docs/records-platform-architecture.md), in order: level 1 is `data`, level
 * 5 is `view`. The one list the setup wire, the compose reader and the server
 * table check against.
 */
export const UNMET_NEED_LEVELS = ['data', 'surface', 'arrangement', 'lifecycle', 'view'] as const;
export type UnmetNeedLevel = (typeof UNMET_NEED_LEVELS)[number];

/** One need the parts could not hold: the level, the kind of business, general words. */
export type ComposedUnmetNeed = {
  readonly level: UnmetNeedLevel;
  readonly archetype: string;
  readonly need: string;
};

/**
 * POST /v1/onboarding/compose body. `clientKey` and `setup` make the
 * business; the two optional members are the business's first memory
 * (docs/plans/agent-business-memory.md, decisions 1, 3 and 13).
 *
 *   understanding   what the interview noted, written as app.business_understanding
 *                   and seeded as the first `business` facts, one per line
 *   transcript      the gather turns, the AI SDK's `UIMessage[]`, stored as a
 *                   thread under the new business with `screen: 'setup'`. Typed
 *                   as `unknown[]` here because this package does not depend
 *                   on `ai`; the route validates it the way chat does
 *   source          `call` when the transcript is the live call's note-taker
 *                   transcript; absent or `interview` otherwise
 *
 * A phone that sends neither still makes a business. The `created: false`
 * replay of a `clientKey` writes none of it a second time.
 */
export type ComposeRequest = {
  readonly clientKey: string;
  readonly setup: ComposedSetup;
  readonly understanding?: SetupUnderstanding;
  readonly transcript?: readonly unknown[];
  readonly source?: 'interview' | 'call';
  /**
   * The thread the client kept the setup's interview under: the transcript
   * is stored as the new business's first thread under this id, so live mode
   * carries on into it after Create (step 5.2). Absent mints one.
   */
  readonly threadId?: string;
  /**
   * What OS learned from the setup files (docs/plans/setup-from-files.md,
   * Decided 8): each row becomes a business fact, with the file it came from,
   * once that file is the new workspace's.
   */
  readonly learned?: readonly { readonly about: string; readonly text: string; readonly fileId: string }[];
  /**
   * The setup files the page still holds (Decided 4): only these become the
   * new workspace's. A file the person removed, or one a reload lost, stays
   * theirs alone. Absent, none is adopted.
   */
  readonly fileIds?: readonly string[];
};

/**
 * The ceilings. Generous for a business anyone would describe in a
 * conversation, and small enough that a runaway model cannot make a body the
 * validator takes a second to walk.
 */
export const COMPOSE_LIMITS = {
  businessName: 120,
  /** Seed rows' text values are words too: the 9.8 board's store with its lists' rows beside a setup at every ceiling (compose-limits.test.ts). */
  words: 8000,
  word: 80,
  records: 40,
  fields: 40,
  links: 40,
  unmetNeeds: 20,
  unmetArchetype: 60,
  unmetNeed: 280,
  /** The rows one list starts with (decision 10); a longer list comes in as an import job after Build (step 8.3). */
  seedRows: 2000,
} as const;

/**
 * The most bytes POST /v1/onboarding/compose reads. A setup at every ceiling
 * (`COMPOSE_LIMITS.records` records of `.fields` fields) measures well under
 * it (compose-limits.test.ts); Fastify's 1 MiB default is too tight for one.
 */
export const COMPOSE_BODY_LIMIT = 4 * 1024 * 1024;

/** A word key: short, lower case, addressable. Dots so keys can be grouped. */
export const WORD_KEY = /^[a-z0-9_.-]{1,80}$/;

const PATTERNS: ReadonlySet<string> = new Set(LINK_PATTERNS);
const MODES: ReadonlySet<string> = new Set(LINK_MODES);
const OPS: ReadonlySet<string> = new Set(LINK_CONDITION_OPS);

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/* ---------------------------------------------------------------- the reader */

export type ComposeRefusal = { code: string; message: string };

export type ComposedSetupResult =
  | { ok: true; value: ComposedSetup }
  | { ok: false; refusal: ComposeRefusal };

const no = (code: string, message: string): ComposedSetupResult => ({
  ok: false,
  refusal: { code, message },
});

/** One link, read without trusting it. Null when any part is not the shape. */
const readLink = (raw: unknown): ComposedLink | null => {
  if (!isObject(raw)) return null;
  const { fromRecordKey, toRecordKey, pattern } = raw;
  if (typeof fromRecordKey !== 'string' || fromRecordKey === '') return null;
  if (typeof toRecordKey !== 'string' || toRecordKey === '') return null;
  if (typeof pattern !== 'string' || !PATTERNS.has(pattern)) return null;

  // The same defaults `readDraft` in the links route gives an absent part.
  const sign = raw.sign ?? 1;
  if (sign !== 1 && sign !== -1) return null;
  const mode = raw.mode ?? 'auto';
  if (typeof mode !== 'string' || !MODES.has(mode)) return null;

  const trigger: ComposedLink['trigger'] = { all: [] };
  if (raw.trigger !== undefined) {
    if (!isObject(raw.trigger)) return null;
    const all = raw.trigger.all ?? [];
    if (!Array.isArray(all)) return null;
    for (const group of all) {
      if (!isObject(group) || !Array.isArray(group.any) || group.any.length === 0) return null;
      const any: ComposedCondition[] = [];
      for (const condition of group.any) {
        if (!isObject(condition)) return null;
        if (typeof condition.fieldKey !== 'string' || condition.fieldKey === '') return null;
        if (typeof condition.op !== 'string' || !OPS.has(condition.op)) return null;
        // Absent is the ordinary condition; present and empty is a half-built hop.
        if (condition.via !== undefined && (typeof condition.via !== 'string' || condition.via === '')) return null;
        any.push({
          fieldKey: condition.fieldKey,
          op: condition.op as LinkConditionOp,
          ...(condition.value === undefined ? {} : { value: condition.value }),
          ...(condition.via === undefined ? {} : { via: condition.via }),
        });
      }
      trigger.all.push({ any });
    }
  }

  const fieldMap: Record<string, ComposedMapping> = {};
  if (raw.fieldMap !== undefined) {
    if (!isObject(raw.fieldMap)) return null;
    for (const [targetKey, mapping] of Object.entries(raw.fieldMap)) {
      if (!isObject(mapping)) return null;
      if (mapping.kind === 'field') {
        if (typeof mapping.fieldKey !== 'string' || mapping.fieldKey === '') return null;
        fieldMap[targetKey] = { kind: 'field', fieldKey: mapping.fieldKey };
        continue;
      }
      if (mapping.kind === 'const' && mapping.value !== undefined) {
        fieldMap[targetKey] = { kind: 'const', value: mapping.value };
        continue;
      }
      if (mapping.kind === 'reference') {
        if (typeof mapping.via !== 'string' || mapping.via === '') return null;
        if (typeof mapping.fieldKey !== 'string' || mapping.fieldKey === '') return null;
        fieldMap[targetKey] = { kind: 'reference', via: mapping.via, fieldKey: mapping.fieldKey };
        continue;
      }
      if (mapping.kind === 'formula') {
        const spec = readFormulaSpec(mapping.spec);
        if (spec === null) return null;
        fieldMap[targetKey] = { kind: 'formula', spec };
        continue;
      }
      return null;
    }
  }

  return {
    fromRecordKey,
    toRecordKey,
    pattern: pattern as LinkPattern,
    sign,
    mode: mode as LinkMode,
    trigger,
    fieldMap,
  };
};

/**
 * The unmet needs of a setup, read without trusting them. A note is never
 * worth the create, so one that is not the shape is dropped rather than
 * refused, and past the ceiling the rest are dropped too. The server judges
 * each once more (a phone number, an email) before it writes it.
 */
export const readUnmetNeeds = (raw: unknown): ComposedUnmetNeed[] => {
  if (!Array.isArray(raw)) return [];
  const out: ComposedUnmetNeed[] = [];
  for (const one of raw) {
    if (out.length === COMPOSE_LIMITS.unmetNeeds) break;
    if (!isObject(one)) continue;
    const { level, archetype, need } = one;
    if (typeof level !== 'string' || !(UNMET_NEED_LEVELS as readonly string[]).includes(level)) continue;
    if (typeof archetype !== 'string' || typeof need !== 'string') continue;
    const kind = archetype.trim();
    const words = need.trim();
    if (kind === '' || words === '' || kind.length > COMPOSE_LIMITS.unmetArchetype || words.length > COMPOSE_LIMITS.unmetNeed) continue;
    out.push({ level: level as UnmetNeedLevel, archetype: kind, need: words });
  }
  return out;
};

/**
 * Read a composed setup off the wire.
 *
 * Shape and ceilings only. Whether the spec is sound is `composeProblems`'
 * question, asked next, and it lists every answer at once; this reader stops
 * at the first fault because there is nothing to list about a body that is
 * not the shape. The spec itself is not walked here beyond its size: the
 * validator already reads every piece of it without trusting any.
 */
export const readComposedSetup = (raw: unknown): ComposedSetupResult => {
  if (!isObject(raw)) return no('SETUP_NOT_OBJECT', 'setup must be an object.');

  const businessName = typeof raw.businessName === 'string' ? raw.businessName.trim() : '';
  if (businessName.length === 0 || businessName.length > COMPOSE_LIMITS.businessName) {
    return no(
      'BUSINESS_NAME_INVALID',
      `businessName must be 1 to ${COMPOSE_LIMITS.businessName} characters.`,
    );
  }

  // Upper-cased here so `isCurrencyCode`, which forgives case, and the column,
  // which does not, agree on what was sent.
  const currency = typeof raw.currency === 'string' ? raw.currency.trim().toUpperCase() : '';
  if (currency === '') return no('CURRENCY_MISSING', 'currency must be a three-letter ISO 4217 code.');

  if (!isObject(raw.words)) {
    return no('WORDS_INVALID', 'words must be an object keyed by the keys the spec carries.');
  }
  const entries = Object.entries(raw.words);
  if (entries.length > COMPOSE_LIMITS.words) {
    return no('WORDS_TOO_MANY', `words holds ${entries.length} entries; the ceiling is ${COMPOSE_LIMITS.words}.`);
  }
  const words: Record<string, string> = {};
  for (const [key, value] of entries) {
    if (!WORD_KEY.test(key)) {
      return no('WORD_KEY_INVALID', `words key "${key}" is not lower case letters, digits, dots, dashes and underscores.`);
    }
    const word = typeof value === 'string' ? value.trim() : '';
    if (word.length === 0 || word.length > COMPOSE_LIMITS.word) {
      return no('WORD_INVALID', `words["${key}"] must be 1 to ${COMPOSE_LIMITS.word} characters.`);
    }
    words[key] = word;
  }

  if (!isObject(raw.spec)) return no('SPEC_INVALID', 'spec must be a template spec object.');
  const records = Array.isArray(raw.spec.records) ? raw.spec.records : [];
  if (records.length > COMPOSE_LIMITS.records) {
    return no('RECORDS_TOO_MANY', `spec declares ${records.length} records; the ceiling is ${COMPOSE_LIMITS.records}.`);
  }
  for (const record of records) {
    const fields = isObject(record) && Array.isArray(record.fields) ? record.fields : [];
    if (fields.length > COMPOSE_LIMITS.fields) {
      return no(
        'FIELDS_TOO_MANY',
        `record "${String(isObject(record) ? record.key : '?')}" declares ${fields.length} fields; the ceiling is ${COMPOSE_LIMITS.fields}.`,
      );
    }
    const seedRows = isObject(record) && Array.isArray(record.seedRows) ? record.seedRows.length : 0;
    if (seedRows > COMPOSE_LIMITS.seedRows) {
      return no(
        'SEED_ROWS_TOO_MANY',
        `list "${String(isObject(record) ? record.key : '?')}" seeds ${seedRows} rows; the ceiling is ${COMPOSE_LIMITS.seedRows}.`,
      );
    }
  }

  const rawLinks = raw.links ?? [];
  if (!Array.isArray(rawLinks)) return no('LINKS_INVALID', 'links must be a list.');
  if (rawLinks.length > COMPOSE_LIMITS.links) {
    return no('LINKS_TOO_MANY', `links holds ${rawLinks.length}; the ceiling is ${COMPOSE_LIMITS.links}.`);
  }
  const links: ComposedLink[] = [];
  for (const [index, rawLink] of rawLinks.entries()) {
    const link = readLink(rawLink);
    if (link === null) {
      return no('LINK_SHAPE', `links[${index}] is not a shape this build reads.`);
    }
    links.push(link);
  }
  const unmetNeeds = readUnmetNeeds(raw.unmetNeeds);

  return {
    ok: true,
    value: { businessName, currency, spec: raw.spec as unknown as TemplateSpec, words, links, ...(unmetNeeds.length === 0 ? {} : { unmetNeeds }) },
  };
};

/* ------------------------------------------------------------- the problems */

/**
 * The catalogue a composed spec resolves against: the shipped words, then the
 * person's. A composed key may not shadow a shipped one, which
 * `composeProblems` refuses, so the order here never decides anything.
 */
export const composedCatalogue = (setup: Pick<ComposedSetup, 'words'>): Catalogue => ({
  ...enFlat,
  ...setup.words,
});

const fieldsOf = (record: TemplateRecordSpec): ReadonlyMap<string, TemplateFieldSpec> =>
  new Map(
    (Array.isArray(record.fields) ? record.fields : [])
      .filter(isObject)
      .map((field) => [String(field.key), field as TemplateFieldSpec]),
  );

const optionKeysOf = (field: TemplateFieldSpec | undefined): ReadonlySet<string> =>
  new Set((Array.isArray(field?.options) ? field.options : []).filter(isObject).map((option) => String(option.key)));

/**
 * A condition's or a constant's value against the select it names.
 *
 * On `is` and `is_not` a composed value names an option KEY, the way a tile
 * condition's `optionKey` does, and apply rewrites it to the id it mints. A
 * key the field does not declare would become a comparison nothing ever
 * satisfies, silently, which is the failure this whole check exists to stop.
 */
const optionProblems = (
  value: unknown,
  field: TemplateFieldSpec,
  what: string,
  path: string,
): TemplateProblem[] => {
  const keys = optionKeysOf(field);
  const held = Array.isArray(value) ? value : [value];
  return held
    .filter((one) => typeof one !== 'string' || !keys.has(one))
    .map((one) => ({
      code: 'LINK_OPTION_UNKNOWN',
      message: `${what} names choice "${String(one)}", which "${field.key}" does not declare.`,
      path,
    }));
};

const linkProblems = (link: ComposedLink, spec: TemplateSpec, path: string, words: Catalogue = {}): TemplateProblem[] => {
  const problems: TemplateProblem[] = [];
  // Read off the wire and stored, but nothing would ever write it: refused
  // here, before the server's own pattern check would roll the create back.
  if (!LINK_PATTERN_ENGINE[link.pattern]) {
    problems.push({
      code: 'LINK_PATTERN_NO_ENGINE',
      message: `A ${link.pattern} link is stored but writes nothing yet; only a feed link runs.`,
      path,
    });
  }
  const records = (Array.isArray(spec.records) ? spec.records : []).filter(isObject) as TemplateRecordSpec[];
  const recordNamed = (key: string, side: string): TemplateRecordSpec | null => {
    const found = records.find((record) => record.key === key);
    // A directory is a list of masters, not a place a movement lands, and
    // nothing is written from one either. The links route offers neither.
    if (found === undefined || found.kind !== 'record') {
      problems.push({
        code: 'LINK_RECORD_UNKNOWN',
        message: `${side} "${key}" is not a record of this spec. A link runs between two records, never a directory.`,
        path,
      });
      return null;
    }
    return found;
  };
  const from = recordNamed(link.fromRecordKey, 'fromRecordKey');
  const to = recordNamed(link.toRecordKey, 'toRecordKey');
  if (from === null || to === null) return problems;
  if (from.key === to.key) {
    problems.push({
      code: 'LINK_SAME_RECORD',
      message: 'A link posts into another record, never into the one it came from.',
      path,
    });
    return problems;
  }

  const fromFields = fieldsOf(from);
  const toFields = fieldsOf(to);
  const unknownField = (key: string, owner: string, what: string): void => {
    problems.push({
      code: 'LINK_FIELD_UNKNOWN',
      message: `${what} names field "${key}", which "${owner}" does not declare.`,
      path,
    });
  };

  const notReference = (via: string, what: string): void => {
    problems.push({
      code: 'LINK_VIA_NOT_REFERENCE',
      message: `"${via}" on "${from.key}" is not a pick from a list, so ${what === 'A trigger condition' ? 'a condition cannot be asked' : 'a fill cannot be read'} through it.`,
      path,
    });
  };
  /**
   * The list a FROM reference picks from, with its fields. Null, with the
   * problem said, when `via` is not a reference of FROM or its list is gone.
   */
  const listThrough = (via: string, what: string): { key: string; fields: ReadonlyMap<string, TemplateFieldSpec> } | null => {
    const hop = fromFields.get(via);
    if (hop === undefined || hop.type !== 'reference') {
      notReference(via, what);
      return null;
    }
    const list = records.find((record) => record.key === hop.targetRecordKey);
    if (list === undefined) {
      unknownField(via, from.key, what);
      return null;
    }
    return { key: list.key, fields: fieldsOf(list) };
  };
  const notFitting = (source: string, targetKey: string): void => {
    problems.push({
      code: 'LINK_FILL_TYPE',
      message: `${source} cannot fill "${targetKey}": the kinds do not match.`,
      path,
    });
  };

  for (const group of link.trigger.all) {
    for (const condition of group.any) {
      if (condition.via !== undefined) {
        const list = listThrough(condition.via, 'A trigger condition');
        if (list === null) continue;
        const field = list.fields.get(condition.fieldKey);
        if (field === undefined) {
          unknownField(condition.fieldKey, list.key, 'A trigger condition');
          continue;
        }
        if ((condition.op === 'is' || condition.op === 'is_not') && field.type === 'select') {
          problems.push(...optionProblems(condition.value, field, 'A trigger condition', path));
        }
        continue;
      }
      const field = fromFields.get(condition.fieldKey);
      if (field === undefined) {
        unknownField(condition.fieldKey, from.key, 'A trigger condition');
        continue;
      }
      if ((condition.op === 'is' || condition.op === 'is_not') && field.type === 'select') {
        problems.push(...optionProblems(condition.value, field, 'A trigger condition', path));
      }
    }
  }

  for (const [targetKey, mapping] of Object.entries(link.fieldMap)) {
    const target = toFields.get(targetKey);
    if (target === undefined) {
      unknownField(targetKey, to.key, 'The field map');
      continue;
    }
    // What Create refuses (`checkSpec` in the server's links.ts), refused here as well, so a draft never holds a link its Build cannot make.
    if (target.type === 'formula') {
      problems.push({ code: 'LINK_FILL_COMPUTED', message: `"${targetKey}" on "${to.key}" is worked out, so no link fills it.`, path });
      continue;
    }
    if (mapping.kind === 'field') {
      const source = fromFields.get(mapping.fieldKey);
      if (source === undefined) {
        unknownField(mapping.fieldKey, from.key, 'The field map');
        continue;
      }
      if (!mappingTypeOk(target.type, source.type)) {
        notFitting(`"${source.key}"`, targetKey);
        continue;
      }
      if (target.type === 'reference' && target.targetRecordKey !== undefined && source.targetRecordKey !== undefined && target.targetRecordKey !== source.targetRecordKey) {
        problems.push({
          code: 'LINK_FILL_LIST',
          message: `"${source.key}" picks from "${source.targetRecordKey}" and "${targetKey}" from "${target.targetRecordKey}": a link carries a pick only into one from the same list.`,
          path,
        });
      }
      continue;
    }
    if (mapping.kind === 'reference') {
      const list = listThrough(mapping.via, 'The field map');
      if (list === null) continue;
      const listField = list.fields.get(mapping.fieldKey);
      if (listField === undefined) {
        unknownField(mapping.fieldKey, list.key, 'The field map');
        continue;
      }
      if (!mappingTypeOk(target.type, listField.type)) notFitting(`"${listField.key}"`, targetKey);
      continue;
    }
    if (mapping.kind === 'formula') {
      for (const key of formulaFieldsRead(mapping.spec)) {
        if (!fromFields.has(key)) unknownField(key, from.key, 'A worked-out fill');
      }
      for (const hop of formulaHopsRead(mapping.spec)) {
        const list = listThrough(hop.via, 'A worked-out fill');
        if (list !== null && !list.fields.has(hop.fieldId)) unknownField(hop.fieldId, list.key, 'A worked-out fill');
      }
      if (aggProblems(mapping.spec).length > 0) {
        problems.push({
          code: 'LINK_FORMULA_LINES',
          message: 'A link cannot add up the lines of an entry yet.',
          path,
        });
      }
      if (!mappingTypeOk(target.type, formulaValueTypeAsFieldType(mapping.spec.result))) {
        notFitting(`A worked-out ${mapping.spec.result}`, targetKey);
      }
      continue;
    }
    if (target.type === 'select') {
      problems.push(...optionProblems(mapping.value, target, `The constant for "${targetKey}"`, path));
      continue;
    }
    const want = constWant(target.type, mapping.value);
    if (want !== null) {
      problems.push({ code: 'LINK_FILL_CONST', message: `The set value for "${targetKey}" on "${to.key}" must be ${want}.`, path });
    }
  }
  // What Create refuses last (`checkSpec`, REQUIRED_UNFILLED): a field the
  // target must have, which no fill answers, refuses every firing. One a
  // show-when rule decides is judged by the firing, on the row it writes; a
  // worked-out, lines or attrs field said required is the template's own
  // refusal (LINES_NEVER_REQUIRED, REQUIRED_IN_ATTRS), never the link's.
  for (const [key, field] of toFields) {
    if (field.required !== true || key in link.fieldMap || field.showWhen !== undefined) continue;
    if (isComputedType(field.type) || field.type === 'lines' || field.storage === 'attr' || field.storage === 'children') continue;
    // Said by the names the person reads, since the studio shows it as the link's reason.
    const name = (labelKey: unknown, fallback: string): string => (typeof labelKey === 'string' ? words[labelKey] : undefined) ?? fallback;
    problems.push({
      code: 'LINK_FILL_REQUIRED',
      message: `${name(to.nameKey, to.key)} needs ${name(field.labelKey, key)} on every entry, and this link fills nothing into it: carry it from ${name(from.nameKey, from.key)}, give it a set value, or make it optional.`,
      path,
    });
  }
  return problems;
};

/**
 * What a set value must be to land in a field of this type, or null when it
 * does: the shape the server's cast (`castScalar` in entry-values.ts) takes,
 * so a link's constant Create would refuse is refused in the draft. A pick
 * from a list is a row's id, which no draft can hold.
 */
const constWant = (type: string, value: unknown): string | null => {
  switch (type) {
    case 'number':
    case 'money':
    case 'quantity':
      return typeof value === 'number' && Number.isFinite(value) ? null : 'a number';
    case 'boolean':
      return typeof value === 'boolean' ? null : 'yes or no';
    case 'text':
    case 'long_text':
    case 'phone':
    case 'location':
      return typeof value === 'string' ? null : 'words';
    case 'date':
      return typeof value === 'string' && isCalendarDay(value) ? null : 'a date written YYYY-MM-DD';
    case 'datetime':
      return typeof value === 'string' && Number.isFinite(Date.parse(value)) ? null : 'a date and time';
    case 'reference':
      return 'carried from a pick on the entry, never set';
    default:
      return null;
  }
};

const isCalendarDay = (value: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match === null) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const utc = new Date(Date.UTC(year, month - 1, day));
  return utc.getUTCFullYear() === year && utc.getUTCMonth() === month - 1 && utc.getUTCDate() === day;
};

/**
 * Every problem with a composed setup, at once. Empty means it may be applied.
 *
 * The spec goes through the same validator the JSON pack does, against the
 * merged catalogue, with `custom` allowed. What this adds is what a composed
 * spec alone can get wrong: a word that shadows a shipped one, a currency the
 * list does not hold, a tier that pretends a template made the field, a noun
 * the device could not say, and a link that names what is not there.
 */
export const composeProblems = (setup: ComposedSetup): TemplateProblem[] => {
  const problems: TemplateProblem[] = [];
  const { spec } = setup;
  const at = COMPOSED_TEMPLATE_ID;

  if (!isObject(spec)) {
    return [{ code: 'SPEC_NOT_OBJECT', message: 'The spec is not an object.', path: at }];
  }
  if (spec.id !== COMPOSED_TEMPLATE_ID) {
    problems.push({
      code: 'SPEC_ID_NOT_COMPOSED',
      message: `A composed spec carries the id "${COMPOSED_TEMPLATE_ID}", not "${String(spec.id)}".`,
      path: at,
    });
  }
  if (spec.version !== 1) {
    problems.push({ code: 'SPEC_VERSION_NOT_ONE', message: 'A composed spec is version 1.', path: at });
  }

  if (!isCurrencyCode(setup.currency)) {
    problems.push({
      code: 'CURRENCY_UNKNOWN',
      message: `"${setup.currency}" is not an active ISO 4217 currency.`,
      path: at,
    });
  }

  // The person's word wins in the merged catalogue, so a composed key that a
  // shipped key already owns would silently rename something every screen
  // says. Refused rather than resolved.
  for (const key of Object.keys(setup.words)) {
    if (enFlat[key] !== undefined) {
      problems.push({
        code: 'WORD_SHADOWS_CATALOGUE',
        message: `words key "${key}" is a shipped catalogue key. A composed word takes a key of its own.`,
        path: `${at}.words[${key}]`,
      });
    }
  }

  // The count rule guards the catalogue card a shipped template shows before
  // anyone picks it. A composed setup may ship without preview rows: the
  // person described the business themselves and the apply never reads them.
  const samples = Array.isArray(spec.sampleEntries) ? spec.sampleEntries.length : 0;
  for (const problem of validateTemplateSpec(spec, composedCatalogue(setup), { allowCustomTier: true })) {
    if (samples === 0 && problem.code === 'SAMPLE_COUNT') continue;
    problems.push(problem);
  }

  const records = (Array.isArray(spec.records) ? spec.records : []).filter(isObject) as TemplateRecordSpec[];
  for (const record of records) {
    for (const field of (Array.isArray(record.fields) ? record.fields : []).filter(isObject) as TemplateFieldSpec[]) {
      // A composed field is the person's own from the moment it exists: the
      // reason `blank.ts` gives, and the same tier it uses. `primary` here
      // would put a template's lock on a field no template made.
      if (field.tier !== 'custom') {
        problems.push({
          code: 'TIER_NOT_CUSTOM',
          message: `Field "${field.key}" has tier "${field.tier}". A composed field is the person's own, so its tier is custom.`,
          path: `${at}.records[${record.key}].fields[${field.key}]`,
        });
      }
    }
  }

  // A stored noun key is a plural STEM: the device says it as
  // `t(key, { count })`, and i18next picks `_one` or `_other` from the count
  // and the language. Both forms have to be shipped or the first record with
  // two entries throws, so the stem is drawn from the shipped nouns until the
  // device reads a composed one.
  for (const key of [spec.nounKey, spec.entryNounKey]) {
    if (typeof key !== 'string') continue;
    if (!nounShipped(key)) {
      problems.push({
        code: 'NOUN_KEY_NOT_SHIPPED',
        message: `Noun "${key}" is not a shipped noun with its one and other forms. The device cannot say it yet; use one of the noun.* keys.`,
        path: at,
      });
    }
  }

  for (const [index, link] of setup.links.entries()) {
    problems.push(...linkProblems(link, spec, `${at}.links[${index}]`, composedCatalogue(setup)));
  }

  return problems;
};
